import { message, Modal } from 'antd'
import React, {useEffect, useRef, useState} from 'react'
import { useNavigate } from 'react-router-dom'
import ActionArea from '../../components/ActionArea'
import Head from '../../components/Head'
import List from '../../components/List'
import Button from '../../components/UI/Button'
import SelectInput from '../../components/UI/SelectInput'
import SmartSearch from '../../components/UI/SmartSearch'
import { getCharitiesList, updateCharityStatus } from '../../services/charities'
import { getPermission } from '../../services/permisssion'
import charitiesStatus from '../../utils/charitiesStatus'
import direction from '../../utils/filterDirectionOrder'
import permissionType from '../../utils/permissionType'
import {
  EditOutlined,
} from '@ant-design/icons'
import { notificationError } from '../../services/notification'
import { useLoading } from '../../context/LoadingProvider/useLoading'
import { IParams } from '../../context/ListRecordProvider/types'
import { useListRecord } from '../../context/ListRecordProvider/useListRecord'
import { sendSystemLog, useAuth } from '../../context/AuthProvider/useAuth'

const selectOptions = [
  {name: 'All', value: 'All'},
  {name: 'Active', value: charitiesStatus.active},
  {name: 'Inactive', value: charitiesStatus.inactive},
  {name: 'Closed', value: charitiesStatus.closed},
]

function Charities(props: {accessLevel: string}) {
  const history = useNavigate()
  const {canEdit} = useAuth()
  const log = {
    log_type: 'visited',
    log_object: 'charities'
  }
  const listRecord = useListRecord()
  const record = listRecord.getRecord('charitiesList')
  const urlParams = new URLSearchParams(window.location.search);
  const useRecord = urlParams.get('useRecord')

  const loading = useLoading()

  const [search, setSearch] = useState('')
  const [params, setParams] = useState<IParams>({
    status: selectOptions[0].value,
    fieldOrder: '',
    directionOrder: '',
    id: null,
    name: '',
    type: '',
  })
  const [validSearch, setValidSearch] = useState(false)
  const [charitiesList, setCharitiesList] = useState({
    count: 0,
    page: null,
    perpage: 0,
    list: [],
  })

  const [modalVisible, setModalVisible] = useState(false)
  const [charityToPreview, setCharityToPreview] = useState<any>("")
  const [previewModal, setPreviewModal] = useState(false)
  const [paramsToChangeStatus, setParamsToChangeStatus] = useState({id: '', status: ''})
  const [changeStatusMessage, setChangeStatusMessage] = useState('The selected charity will change. Are you sure?')


  useEffect( () => {
    sendSystemLog(log)
  }, [])
  
  useEffect(() => {
    (async () => {
      loading.addLoading()
      // @ts-ignore
      //inputRef.current.focus();
      if(record && useRecord) {
        const response = await getCharitiesList({
          status: record.status,
          [`${record.type}Id`]: record.id,
        })
        setCharitiesList(response)
        setParams(record)
        setSearch(record.name)
      }
      loading.removeLoading()

    })()
  }, [])


  const onRefresh = async (order?: string, directionparam?: string) => {
    if(!validSearch || !params.id) return notificationError("Please select a valid search")
    loading.addLoading()
    let requestParams = {
      status: params.status,
      [`${params.type}Id`]: params.id,
      fieldOrder: '',
      directionOrder: ''
    }
    if(order && directionparam) {
      requestParams = {
        ...requestParams,
        fieldOrder: order || '',
        directionOrder: directionparam === 'ascend' ? direction.asc : direction.desc,
      }
      setParams({
        ...params,
        fieldOrder: order || '',
        directionOrder: directionparam === 'ascend' ? direction.asc : direction.desc,
      })
    }
    
    const response = await getCharitiesList(requestParams)
    loading.removeLoading()
    listRecord.addRecord('charitiesList', params)
    return setCharitiesList(response)

  }

  const onChangeOrder = (order: {column: any, field: string, order: string| undefined}) => {
    if(order.column) return onRefresh(order.field, order.order)
    return onRefresh()
  }

  const getStatusLabel = (status: string) => {
    if(status === 'ACTIVE') {
      return <div className="cashiers__table__status cashiers__table__status--active">{status}</div>
    } else {
      return <div className="cashiers__table__status cashiers__table__status--inactive">{status}</div>
    }
  }

  const getActionButtons = (value: any) => (
    <div className="cashiers__table__actions">
      <Button 
        onClick={() =>{
          setCharityToPreview(value)
          setPreviewModal(true)
        }}
        style={{backgroundColor: '#73a839'}}
        type='primary'
      >
        <p>Details</p>
      </Button>
      <Button 
        onClick={() => openChangeStatusModal(value.id, value.status)}
        style={{
          backgroundColor: (value.status === charitiesStatus.active && '#dd5600') || 
          (value.status === charitiesStatus.closed && '#bd4247') || '#669533',
          width: 91
        }}
        type='primary'
        disabled={!canEdit}
      >
        <p>{value.status === charitiesStatus.active ? 'Close' : value.status === charitiesStatus.closed ? 'Deactivate' : 'Activate' }</p>
      </Button>
      <Button 
        onClick={() => history(`../charities/new/${value.id}`)}
        type='primary'
        style={{backgroundColor: '#2fa4e7'}}
        disabled={!canEdit}
      >
        <EditOutlined style={{ fontSize: '16px', color: '#FFF' }} />
      </Button>
    </div>
  )

  const openChangeStatusModal = (id: string, status: string) => {
    const params = {
      id,
      status: status === charitiesStatus.active ? charitiesStatus.closed : status === charitiesStatus.closed ? charitiesStatus.inactive : charitiesStatus.active
    }
    setChangeStatusMessage(params.status === charitiesStatus.closed ? 'The selected charity, any child location and related devices and cashiers will be closed. Are you sure?' : 'The selected charity will change. Are you sure?')
    setParamsToChangeStatus(params)
    setModalVisible(true)
  }

  const changeLocationStatus = async () => {
    await updateCharityStatus(paramsToChangeStatus)
    await onRefresh()
    setModalVisible(false)
  }

  return (
    <div className="charities">
      <Modal 
        title={changeStatusMessage}
        open={modalVisible}
        onOk={changeLocationStatus} 
        onCancel={() => setModalVisible(false)}
      />
      <Modal 
        title={charityToPreview.name}
        open={previewModal}
        onOk={() => setPreviewModal(false)}
        onCancel={() => setPreviewModal(false)} 
        centered
      >
        <div className="charities__preview">
          <p><strong>Name:</strong> {charityToPreview.name || '--'}</p>
          <p><strong>Customer ID:</strong> {charityToPreview.customerId || '--'}</p>
          <p><strong>Address:</strong> {charityToPreview.address || '--'}</p>
          <p><strong>Zipcode:</strong> {charityToPreview.zipcode || '--'}</p>
          <p><strong>City:</strong> {charityToPreview.city || '--'}</p>
          <p><strong>State:</strong> {charityToPreview.state || '--'}</p>
          <p><strong>Phone:</strong> {charityToPreview.phone || '--'}</p>
          <p><strong>Emails:</strong> {charityToPreview.email || '--'}</p>
          <p><strong>Status:</strong> {charityToPreview.status || '--'}</p>
          <p><strong>Distributor:</strong> {charityToPreview.distributorName || '--'}</p>
        </div>
      </Modal>
      <Head title="All charities" buttonText="New Charity" disabled={!canEdit} buttonAction={() => history('/charities/new')} />
      <meta name="viewport" content="width=device-width, initial-scale=.75"/>
      <ActionArea buttonAction={onRefresh} buttonText={'Search'} disabled={!validSearch}>
        <div className="cashiers__action-area">
          <SmartSearch
            value={search}
            onChange={(e: string) => setSearch(e)}
            onValidSearch={setValidSearch}
            triggerWith={record?.name || ''}
            label='Smart Search'
            status="active,closed,inactive"
            searchBy={['distributor', 'charity']}
            onObjValueChange={(e: any) => setParams({...params, ...e})}
            autoFocus
          />
          <SelectInput 
            options={selectOptions} 
            label='Status'
            onSelectChange={(e) => setParams({...params, status: e?.value ? e.value : e})}
            value={params.status}
          />
        </div>
      </ActionArea>
      <List
        rows={charitiesList.list}
        onChange={(selectedRowKeys, selectedRows, order) => onChangeOrder(order)}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'City',
            dataIndex: 'city',
            key: 'city',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'State',
            dataIndex: 'state',
            key: 'state',
            render: (value: string) => value,
            sorter: true,
            width: 100
          },
          {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => getStatusLabel(value),
            sorter: true,
            width: 130
          },
          {
            title: 'Distributor',
            dataIndex: 'distributorName',
            key: 'distributorName',
            render: (value: string) => value,
            sorter: true,
            width: 200
          },
          {
            title: 'Actions',
            dataIndex: '',
            key: '',
            render: (value: any) => getActionButtons(value),
            width: 200
          },
        ]}
      />
    </div>
  )
}

export default Charities